"use client";

import { Link } from "@/i18n/navigation";
import { ArrowRightIcon, MapPinIcon } from "@heroicons/react/24/outline";
import Contact from "@/components/Contact/Contact";
import { bruxellesData } from "@/lib/communesBruxelles";

const CONTENT = {
  fr: {
    badge: "19 communes desservies",
    h1: "Vide maison à Bruxelles, commune par commune",
    intro:
      "Chaque commune bruxelloise a ses spécificités — accès, type de bâti, stationnement. Choisissez la vôtre pour une information adaptée à votre quartier.",
    seePage: "Voir la page",
  },
  nl: {
    badge: "19 bediende gemeenten",
    h1: "Huis leegmaken in Brussel, gemeente per gemeente",
    intro:
      "Elke Brusselse gemeente heeft haar eigen kenmerken — toegang, type bebouwing, parkeermogelijkheden. Kies uw gemeente voor informatie die is afgestemd op uw buurt.",
    seePage: "Bekijk de pagina",
  },
  en: {
    badge: "19 communes served",
    h1: "House clearance in Brussels, commune by commune",
    intro:
      "Every Brussels commune has its own characteristics — access, type of buildings, parking. Choose yours for information tailored to your neighbourhood.",
    seePage: "View the page",
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];
  const communes = Object.entries(bruxellesData);

  return (
    <div>
      <section className="relative overflow-hidden bg-sand py-20 text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 text-center lg:px-10">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-accent">
            <MapPinIcon className="h-4 w-4" />
            {c.badge}
          </div>
          <h1 className="mt-6 font-serif text-4xl font-black md:text-5xl">
            {c.h1}
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-ink/65">
            {c.intro}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {communes.map(([slug, commune]) => {
              const displayName = commune.content[locale]?.displayName ?? commune.displayName;
              const intro = commune.content[locale]?.intro ?? commune.content.fr.intro;
              return (
                <Link
                  key={slug}
                  href={`/vide-maison/${slug}`}
                  className="group flex flex-col rounded-3xl border border-sand-line bg-sand p-6 transition hover:-translate-y-1 hover:border-accent hover:bg-white hover:shadow-[0_20px_50px_rgba(28,37,33,0.10)]"
                >
                  <p className="font-serif text-lg font-bold text-ink">{displayName}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-accent">
                    {commune.codePostal}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600 line-clamp-2">
                    {intro}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-accent">
                    {c.seePage}
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
